import React, { useEffect, useMemo, useState } from 'react';
import { Info, Plus } from 'lucide-react';
import { get, patch, postJson } from '../lib/apiClient';
import {
  getExchangeRate,
  updateExchangeRate,
  getExchangeRateHistory,
  type ExchangeRateHistoryEntry,
  getVatRate,
  getCompetitorMarkupWarn,
  updateCompetitorMarkupWarn,
  updateVatRate,
  getVatRateHistory,
  type VatRateHistoryEntry,
  getIfsRfqSettings,
  updateIfsRfqSettings,
  getEmailInboxes,
  updateEmailInboxes,
  type IfsRfqSettings,
  type EmailInboxConfig,
  type EmailInboxType,
  type EmailSubjectRule,
} from '../lib/settingsApi';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
// The category vocabulary a catalogue item may be filed under. Managed here
// because it is a fixed list an ADMIN maintains — the owner's decision on
// 2026-09-07 — and Settings is where this app's admin-maintained lists live.
import {
  ItemCategory,
  listItemCategories,
  createItemCategory,
  updateItemCategory,
  deleteItemCategory,
} from '../lib/itemCategoryApi';
import { formatDateTime as fmtDateTime } from "../utils/dateFormat";
import { Modal } from '../components/Modal';
import Button from '../components/ui/Button';
import SegmentedControl from '../components/ui/SegmentedControl';
import { ErrorState } from '../components/ui/States';
import StatusBadge from '../components/StatusBadge';
import { useConfirm } from '../components/ui/ConfirmProvider';
import CreditReasonsSettings from '../features/creditNotes/CreditReasonsSettings';

const SETTINGS_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>Configure system-wide settings for the application.</p>
    <p className="font-medium">Tabs:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li><strong>Price list settings:</strong> Vendor / price list keywords and matching (JSON)</li>
      <li><strong>Portal automation:</strong> Portal credentials and automation rules</li>
      <li><strong>Exchange rate:</strong> TTD to USD rate for currency conversion</li>
      <li><strong>RFQ processing:</strong> IFS RFQ matching (domains, subject keywords)</li>
    </ul>
    <p>Use Refresh to load current values and Save to apply changes.</p>
  </div>
);

type TabKey =
  | 'priceList'
  | 'portalAutomation'
  | 'exchangeRate'
  | 'rfqProcessing'
  | 'emailInboxes'
  | 'itemCategories'
  | 'creditReasons';

// The tab strip, as the app's one segmented control. Order unchanged.
const SETTINGS_TABS: { value: TabKey; label: string }[] = [
  { value: 'priceList', label: 'Price list settings' },
  { value: 'portalAutomation', label: 'Portal automation' },
  { value: 'exchangeRate', label: 'Exchange rate' },
  { value: 'rfqProcessing', label: 'RFQ processing' },
  { value: 'emailInboxes', label: 'Email inboxes' },
  { value: 'itemCategories', label: 'Item categories' },
  { value: 'creditReasons', label: 'Credit note reasons' },
];

type PortalMatchType = 'fromEmail' | 'fromDomain' | 'portalDomain';

type PortalType =
  | 'oceanic'
  | 'procureship'
  | 'shipserv'
  | 'portal4'
  | 'emder'
  | 'emder_food'
  | 'emder_stores';

type CredentialsMode = 'static' | 'one_time_email' | 'env_fallback';

type OneTimePatterns = {
  usernameRegex: string | null;
  passwordRegex: string | null;
  otpRegex: string | null;
};

type PortalAutomationSetting = {
  _id: string;
  matchType: PortalMatchType;
  matchValue: string;
  portalType: string;

  // ✅ NEW from API
  credentialsMode?: CredentialsMode;
  portalUrlOverride?: string | null;
  oneTimePatterns?: OneTimePatterns;

  username: string | null;
  hasPassword: boolean;
  enabled: boolean;
  notes: string | null;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  lastError: string | null;
  createdAt?: string;
  updatedAt?: string;
};

type PortalFormState = {
  _id?: string;
  matchType: PortalMatchType;
  matchValue: string;
  portalType: PortalType;

  // ✅ NEW
  credentialsMode: CredentialsMode;
  portalUrlOverride: string;
  oneTimePatterns: {
    usernameRegex: string;
    passwordRegex: string;
    otpRegex: string;
  };

  username: string;
  password: string; // only sent when non-empty
  enabled: boolean;
  notes: string;
};

type TestRunResult = {
  ok: boolean;
  mode: 'download_only' | 'create_rfq';
  portalType: string;
  credsSource: string | null;
  effectiveUrl: string | null;
  downloaded: boolean;
  filename: string | null;
  mimeType: string | null;
  bytes: number;
  uniqueKey: string | null;
  portalKey: string | null;
  rfqIdFromClient: string | null;
  rfqCreated: boolean;
  rfqId: string | null;
  durationMs: number;
  note?: string;
};

type EmailInboxForm = Omit<EmailInboxConfig, 'imap'> & {
  imap: EmailInboxConfig['imap'] & { password?: string };
};

const portalTypes: { value: PortalType; label: string }[] = [
  { value: 'oceanic', label: 'Oceanic' },
  { value: 'procureship', label: 'ProcureShip' },
  { value: 'shipserv', label: 'ShipServ' },
  { value: 'portal4', label: 'Portal 4 (one-time creds email body)' },
  { value: 'emder', label: 'Emder (generic)' },
  { value: 'emder_food', label: 'Emder Food' },
  { value: 'emder_stores', label: 'Emder Stores' },
];

const matchTypes: { value: PortalMatchType; label: string; hint: string }[] = [
  {
    value: 'fromEmail',
    label: 'From Email (exact)',
    hint: 'Matches emailDoc.from exactly (e.g. noreply@mail.procureship.com)',
  },
  {
    value: 'fromDomain',
    label: 'From Domain',
    hint: 'Matches domain of emailDoc.from (e.g. procureship.com)',
  },
  {
    value: 'portalDomain',
    label: 'Portal Link Domain',
    hint: 'Matches domain of emailDoc.portal.link (e.g. procureship.com)',
  },
];

const credentialsModes: { value: CredentialsMode; label: string; hint: string }[] =
  [
    {
      value: 'static',
      label: 'Static (stored username/password)',
      hint: 'Uses saved username + encrypted password from Settings.',
    },
    {
      value: 'one_time_email',
      label: 'One-time creds from email body',
      hint: 'Extract credentials from email body using regex patterns.',
    },
    {
      value: 'env_fallback',
      label: 'Env fallback (legacy)',
      hint: 'Uses legacy .env or portal.meta fallback credentials.',
    },
  ];

const inboxTypeOptions: { value: EmailInboxType; label: string }[] = [
  { value: 'rfq', label: 'RFQ' },
  { value: 'quotation', label: 'Quotations' },
  { value: 'price_list', label: 'Price List' },
  { value: 'confirmed_order', label: 'Confirmed Orders' },
  { value: 'others', label: 'Others' },
];

function formatDt(s: string | null | undefined) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return '—';
    return fmtDateTime(d);
  } catch {
    return '—';
  }
}

function normalizeErrorMessage(e: any) {
  const msg = e?.message || '';
  // backend sometimes returns raw text; keep short
  return msg.length > 280 ? msg.slice(0, 280) + '…' : msg || 'Something went wrong';
}

function safeTrim(v: string) {
  return String(v || '').trim();
}

function safeLower(v: string) {
  return safeTrim(v).toLowerCase();
}

function parseListInput(text: string) {
  return String(text || '')
    .split(/[\n,]/g)
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}

function joinList(list: string[] | null | undefined) {
  return (Array.isArray(list) ? list : []).join('\n');
}

function normalizeRegexInput(v: string) {
  const t = safeTrim(v);
  return t.length ? t : null;
}

function isValidJsonText(s: string) {
  try {
    JSON.parse(s);
    return true;
  } catch {
    return false;
  }
}

export const SettingsPage: React.FC = () => {
  const confirm = useConfirm();

  // Tabs
  const [activeTab, setActiveTab] = useState<TabKey>('priceList');
  // Bumped by Refresh on the Credit note reasons tab; that screen reloads itself.
  const [creditReasonsReload, setCreditReasonsReload] = useState(0);

  // Global UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // -----------------------------
  // Price List Settings (existing)
  // -----------------------------
  const [rawJson, setRawJson] = useState('{\n}');
  const [lastLoadedJson, setLastLoadedJson] = useState('{\n}');
  const rawJsonIsValid = useMemo(() => isValidJsonText(rawJson), [rawJson]);
  const rawJsonCharCount = rawJson.length;

  const fetchPriceListSettings = async () => {
    setLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res: any = await get<any>('/price-list-settings');

      const settings = res?.data?.settings ?? res?.settings ?? res?.data ?? res ?? {};
      const formatted = JSON.stringify(settings, null, 2);

      setRawJson(formatted);
      setLastLoadedJson(formatted);
    } catch (e: any) {
      console.error('Failed to fetch price list settings', e);
      setError(normalizeErrorMessage(e) || 'Failed to load settings');
      setRawJson('{\n}');
      setLastLoadedJson('{\n}');
    } finally {
      setLoading(false);
    }
  };

  const onSavePriceListSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const parsed = JSON.parse(rawJson);
      await patch('/price-list-settings', parsed);

      setSuccessMsg('Price list settings saved.');
      await fetchPriceListSettings();
    } catch (e: any) {
      console.error('Failed to save price list settings', e);
      if (e?.message?.includes('Unexpected token') || !rawJsonIsValid) {
        setError('Invalid JSON. Please fix JSON format and try again.');
      } else {
        setError(normalizeErrorMessage(e) || 'Failed to save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  const onFormatJson = () => {
    setError(null);
    setSuccessMsg(null);
    try {
      const parsed = JSON.parse(rawJson);
      setRawJson(JSON.stringify(parsed, null, 2));
      setSuccessMsg('JSON formatted.');
    } catch {
      setError('Cannot format: JSON is invalid.');
    }
  };

  const onMinifyJson = () => {
    setError(null);
    setSuccessMsg(null);
    try {
      const parsed = JSON.parse(rawJson);
      setRawJson(JSON.stringify(parsed));
      setSuccessMsg('JSON minified.');
    } catch {
      setError('Cannot minify: JSON is invalid.');
    }
  };

  const onCopyJson = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await navigator.clipboard.writeText(rawJson);
      setSuccessMsg('Copied JSON to clipboard.');
    } catch {
      setError('Failed to copy to clipboard.');
    }
  };

  const onResetToLastLoaded = () => {
    setError(null);
    setSuccessMsg(null);
    setRawJson(lastLoadedJson || '{\n}');
    setSuccessMsg('Reset to last loaded settings.');
  };

  // -----------------------------
  // Portal Automation Settings
  // -----------------------------
  const [portalRows, setPortalRows] = useState<PortalAutomationSetting[]>([]);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalSaving, setPortalSaving] = useState(false);

  const [portalModalOpen, setPortalModalOpen] = useState(false);
  const [portalModalMode, setPortalModalMode] = useState<'create' | 'edit'>('create');

  // ✅ test run UI state
  const [testRunningId, setTestRunningId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestRunResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testModalOpen, setTestModalOpen] = useState(false);

  const defaultPortalForm: PortalFormState = useMemo(
    () => ({
      matchType: 'fromEmail',
      matchValue: '',
      portalType: 'procureship',

      credentialsMode: 'static',
      portalUrlOverride: '',
      oneTimePatterns: {
        usernameRegex: '',
        passwordRegex: '',
        otpRegex: '',
      },

      username: '',
      password: '',
      enabled: true,
      notes: '',
    }),
    []
  );

  const [portalForm, setPortalForm] = useState<PortalFormState>(defaultPortalForm);

  const showOneTimeSection = useMemo(() => {
    return portalForm.credentialsMode === 'one_time_email' || portalForm.portalType === 'portal4';
  }, [portalForm.credentialsMode, portalForm.portalType]);

  // -----------------------------
  // Exchange Rate Settings (new)
  // -----------------------------
  const [exchangeRate, setExchangeRate] = useState<number>(0.1667); // TTD to USD
  const [usdToTtd, setUsdToTtd] = useState<number>(6); // USD to TTD
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const [exchangeRateSaving, setExchangeRateSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  // Change log — every rate change is recorded with its effective window.
  const [rateHistory, setRateHistory] = useState<ExchangeRateHistoryEntry[]>([]);
  const [rateHistoryLoading, setRateHistoryLoading] = useState(false);
  const [rateNote, setRateNote] = useState('');

  const fetchRateHistory = async () => {
    setRateHistoryLoading(true);
    try {
      setRateHistory(await getExchangeRateHistory(50));
    } catch (e: any) {
      console.error('Failed to fetch exchange rate history', e);
    } finally {
      setRateHistoryLoading(false);
    }
  };

  const fetchExchangeRate = async () => {
    setExchangeRateLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const data = await getExchangeRate();
      setExchangeRate(data.rate ?? 0); // D102.7: null = not set yet; 0 keeps Save disabled until one is typed
      setUsdToTtd(data.usdToTtd ?? 0);
      setLastUpdated(data.lastUpdated);
      setUpdatedBy(data.updatedBy);
    } catch (e: any) {
      console.error('Failed to fetch exchange rate', e);
      setError(normalizeErrorMessage(e) || 'Failed to load exchange rate');
    } finally {
      setExchangeRateLoading(false);
    }
    fetchRateHistory();
  };

  const saveExchangeRate = async () => {
    setExchangeRateSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      if (usdToTtd <= 0) {
        setError('Exchange rate must be greater than 0');
        setExchangeRateSaving(false);
        return;
      }

      // Spell out the blast radius before writing — the change is forward-only, but
      // that is exactly the thing an admin needs to be sure of.
      const confirmed = await confirm({
        title: `Set the exchange rate to 1 USD = TT$${usdToTtd.toFixed(4)}?`,
        summary:
          'This applies to quotations generated from now on. Existing quotations, ' +
          'confirmed orders and their purchase orders keep the rate they were created with.',
        confirmLabel: 'Set rate',
      });
      if (!confirmed) {
        setExchangeRateSaving(false);
        return;
      }

      const ttdToUsdRate = 1 / usdToTtd;

      const data = await updateExchangeRate(ttdToUsdRate, 'admin', rateNote.trim());
      setExchangeRate(data.rate ?? 0);
      setUsdToTtd(data.usdToTtd ?? 0);
      setLastUpdated(data.lastUpdated);
      setUpdatedBy(data.updatedBy);
      setRateNote('');
      setSuccessMsg(
        'Exchange rate updated. It applies to new quotations only — existing quotations and confirmed orders are unchanged.'
      );
      fetchRateHistory();
    } catch (e: any) {
      console.error('Failed to save exchange rate', e);
      setError(normalizeErrorMessage(e) || 'Failed to save exchange rate');
    } finally {
      setExchangeRateSaving(false);
    }
  };

  // -----------------------------
  // VAT rate (owner decision 2026-09-24, "one Trinidad VAT rate") — a
  // self-contained section beside the exchange rate above: its own fetch,
  // edit and save, never wired into the header Save/Refresh (which dispatch
  // ONE action per tab and already mean "the exchange rate" on this tab).
  // The government can change VAT, so this is the ONLY place a Trinidad VAT
  // percentage is ever typed in this codebase — every reader
  // (`costPriceTiers.catalogueRowCostPrice` and its callers) takes it from
  // here, never a literal.
  // -----------------------------
  const [vatRatePercent, setVatRatePercent] = useState<number | null>(null);
  const [vatRateInput, setVatRateInput] = useState<string>('');
  const [vatRateLoading, setVatRateLoading] = useState(false);
  const [vatRateSaving, setVatRateSaving] = useState(false);
  const [vatRateLoaded, setVatRateLoaded] = useState(false);
  const [vatRateUpdatedAt, setVatRateUpdatedAt] = useState<string | null>(null);
  const [vatRateUpdatedBy, setVatRateUpdatedBy] = useState<string | null>(null);
  const [vatRestatedMsg, setVatRestatedMsg] = useState<string | null>(null);
  const [vatRateHistory, setVatRateHistory] = useState<VatRateHistoryEntry[]>([]);
  const [vatRateHistoryLoading, setVatRateHistoryLoading] = useState(false);

  const fetchVatRateHistory = async () => {
    setVatRateHistoryLoading(true);
    try {
      setVatRateHistory(await getVatRateHistory());
    } catch (e: any) {
      console.error('Failed to fetch VAT rate history', e);
    } finally {
      setVatRateHistoryLoading(false);
    }
  };

  const fetchVatRate = async () => {
    setVatRateLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const data = await getVatRate();
      setVatRatePercent(data.ratePercent);
      setVatRateInput(data.ratePercent != null ? String(data.ratePercent) : '');
      setVatRateUpdatedAt(data.updatedAt);
      setVatRateUpdatedBy(data.updatedBy);
      setVatRateLoaded(true);
    } catch (e: any) {
      console.error('Failed to fetch VAT rate', e);
      setError(normalizeErrorMessage(e) || 'Failed to load VAT rate');
    } finally {
      setVatRateLoading(false);
    }
    fetchVatRateHistory();
  };

  // Competitor-markup warning threshold (D97): quotation lines whose competitor price marks
  // up our cost by less than this percent are flagged red on the quotation grid.
  const [warnInput, setWarnInput] = useState<string>('');
  const [warnPercent, setWarnPercent] = useState<number | null>(null);
  const [warnLoading, setWarnLoading] = useState(false);
  const [warnSaving, setWarnSaving] = useState(false);

  const fetchCompetitorWarn = async () => {
    setWarnLoading(true);
    try {
      const data = await getCompetitorMarkupWarn();
      setWarnPercent(data.percent);
      setWarnInput(String(data.percent));
    } catch (e: any) {
      console.error('Failed to load competitor markup threshold', e);
      setError('Failed to load the competitor markup threshold.');
    } finally {
      setWarnLoading(false);
    }
  };

  const saveCompetitorWarn = async () => {
    const parsed = parseFloat(warnInput);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1000) {
      setError('The competitor markup threshold must be a number from 0 to 1000.');
      return;
    }
    setWarnSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const data = await updateCompetitorMarkupWarn(parsed);
      setWarnPercent(data.percent);
      setWarnInput(String(data.percent));
      setSuccessMsg('Competitor markup threshold updated.');
    } catch (e: any) {
      console.error('Failed to save competitor markup threshold', e);
      // Switch on the machine-readable code, never on the message prose.
      setError(
        e?.code === 'COMPETITOR_MARKUP_WARN_INVALID'
          ? 'The competitor markup threshold must be a number from 0 to 1000.'
          : 'Failed to save the competitor markup threshold.'
      );
    } finally {
      setWarnSaving(false);
    }
  };

  const saveVatRate = async () => {
    setVatRestatedMsg(null);
    const parsed = parseFloat(vatRateInput);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed >= 100) {
      setError('VAT rate must be a number from 0 up to (but not including) 100.');
      return;
    }
    setVatRateSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const confirmed = await confirm({
        title: `Set the VAT rate to ${parsed}%?`,
        summary:
          'This is the ONE VAT rate the whole app costs a VAT-applicable Master Catalogue item at — ' +
          'every such item is re-priced at this rate immediately. It never changes what a vessel is ' +
          'charged (vessels are never charged VAT).',
        confirmLabel: 'Set rate',
      });
      if (!confirmed) {
        setVatRateSaving(false);
        return;
      }

      const data = await updateVatRate(parsed);
      setVatRatePercent(data.ratePercent);
      setVatRateInput(data.ratePercent != null ? String(data.ratePercent) : '');
      setVatRateUpdatedAt(data.updatedAt);
      setVatRateUpdatedBy(data.updatedBy);
      setVatRestatedMsg(`${data.restatedRows} catalogue item${data.restatedRows === 1 ? '' : 's'} re-priced.`);
      setSuccessMsg('VAT rate updated.');
      fetchVatRateHistory();
    } catch (e: any) {
      console.error('Failed to save VAT rate', e);
      setError(normalizeErrorMessage(e) || 'Failed to save VAT rate');
    } finally {
      setVatRateSaving(false);
    }
  };

  // -----------------------------
  // IFS RFQ Settings
  // -----------------------------
  const [ifsSettings, setIfsSettings] = useState<IfsRfqSettings>({
    enabled: false,
    matchFromEmailDomains: [],
    matchSubjectKeywords: [],
    priceColumnKeywords: [],
    treatZeroAsMissing: true,
  });
  const [ifsDomainsText, setIfsDomainsText] = useState('');
  const [ifsSubjectText, setIfsSubjectText] = useState('');
  const [ifsPriceKeysText, setIfsPriceKeysText] = useState('');
  const [ifsLoading, setIfsLoading] = useState(false);
  const [ifsSaving, setIfsSaving] = useState(false);
  const [ifsLoaded, setIfsLoaded] = useState(false);

  // -----------------------------
  // Email Inbox Settings
  // -----------------------------
  const [emailInboxes, setEmailInboxes] = useState<EmailInboxForm[]>([]);
  const [emailInboxesLoading, setEmailInboxesLoading] = useState(false);
  const [emailInboxesSaving, setEmailInboxesSaving] = useState(false);
  const [emailInboxesLoaded, setEmailInboxesLoaded] = useState(false);
  const [emailSubjectRules, setEmailSubjectRules] = useState<EmailSubjectRule[]>([]);
  const [emailSubjectRuleInputs, setEmailSubjectRuleInputs] = useState<string[]>([]);

  // ── Item Categories ────────────────────────────────────────────────────────
  //
  // The vocabulary a Master Catalogue row's `category` may be set to. It exists
  // because the field was uncontrolled free text and the live filter held 19
  // values for six real ones — three casings of "Electronics", two of
  // "Provisions", the literal string `Null`, and five pieces of typing-test junk
  // beside the client's actual ship departments. She groups a price list by
  // category and splits an order on it, because the vessel's owner pays for some
  // departments and the charterer for others, so it has to mean one thing.
  //
  // ADMIN-EDITABLE and not hardcoded: the food groups (beef, pork, chicken,
  // deli, dairy, cheese, cold cuts) arrive with her price list and must not need
  // a deploy. The six built-ins cannot be edited or removed — they have no id.
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);
  const [newCategoryLabel, setNewCategoryLabel] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editingCategoryLabel, setEditingCategoryLabel] = useState('');

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res = await listItemCategories();
      setCategories(res.data?.categories || []);
      setCategoriesLoaded(true);
    } catch (e: any) {
      console.error('Failed to fetch item categories', e);
      setError(normalizeErrorMessage(e) || 'Failed to load item categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const onAddCategory = async () => {
    const label = newCategoryLabel.trim();
    if (!label) return;
    setCategorySaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await createItemCategory({ label });
      setNewCategoryLabel('');
      setSuccessMsg(`Added "${label}".`);
      await fetchCategories();
    } catch (e: any) {
      // The server's own sentence — including the 409 the UNIQUE INDEX
      // produced when two people added the same word at once.
      setError(normalizeErrorMessage(e) || 'Could not add that category');
    } finally {
      setCategorySaving(false);
    }
  };

  const onRenameCategory = async (id: string) => {
    const label = editingCategoryLabel.trim();
    if (!label) return;
    setCategorySaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await updateItemCategory(id, { label });
      setEditingCategoryId(null);
      setEditingCategoryLabel('');
      await fetchCategories();
    } catch (e: any) {
      setError(normalizeErrorMessage(e) || 'Could not rename that category');
    } finally {
      setCategorySaving(false);
    }
  };

  const onDeleteCategory = async (cat: ItemCategory) => {
    if (!cat._id) return;
    // Said plainly, because it is the surprising half: removing the word from
    // the list does NOT un-file the items already under it. Those rows keep
    // their value and start showing as "not in list", which is visible and
    // undone by adding the category back.
    const ok = await confirm({
      title: `Remove "${cat.label}" from the category list?`,
      summary:
        'Items already filed under it keep their category and will show as "not in list" ' +
        'until they are re-filed. Nothing is deleted or re-categorised.',
      tone: 'error',
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    setCategorySaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await deleteItemCategory(cat._id);
      setSuccessMsg(`Removed "${cat.label}" from the list.`);
      await fetchCategories();
    } catch (e: any) {
      setError(normalizeErrorMessage(e) || 'Could not remove that category');
    } finally {
      setCategorySaving(false);
    }
  };

  const fetchIfsSettings = async () => {
    setIfsLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const data = await getIfsRfqSettings();
      setIfsSettings(data);
      setIfsDomainsText(joinList(data.matchFromEmailDomains));
      setIfsSubjectText(joinList(data.matchSubjectKeywords));
      setIfsPriceKeysText(joinList(data.priceColumnKeywords));
      setIfsLoaded(true);
    } catch (e: any) {
      console.error('Failed to fetch IFS RFQ settings', e);
      setError(normalizeErrorMessage(e) || 'Failed to load IFS RFQ settings');
    } finally {
      setIfsLoading(false);
    }
  };

  const saveIfsSettings = async () => {
    setIfsSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const payload: IfsRfqSettings = {
        enabled: ifsSettings.enabled,
        matchFromEmailDomains: parseListInput(ifsDomainsText).map((d) => d.toLowerCase()),
        matchSubjectKeywords: parseListInput(ifsSubjectText).map((k) => k.toLowerCase()),
        priceColumnKeywords: parseListInput(ifsPriceKeysText).map((k) => k.toLowerCase()),
        treatZeroAsMissing: ifsSettings.treatZeroAsMissing,
      };

      const saved = await updateIfsRfqSettings(payload);
      setIfsSettings(saved);
      setIfsDomainsText(joinList(saved.matchFromEmailDomains));
      setIfsSubjectText(joinList(saved.matchSubjectKeywords));
      setIfsPriceKeysText(joinList(saved.priceColumnKeywords));
      setSuccessMsg('IFS RFQ settings saved.');
    } catch (e: any) {
      console.error('Failed to save IFS RFQ settings', e);
      setError(normalizeErrorMessage(e) || 'Failed to save IFS RFQ settings');
    } finally {
      setIfsSaving(false);
    }
  };


  const fetchEmailInboxSettings = async () => {
    setEmailInboxesLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const response = await getEmailInboxes();
      const rows = response.inboxes || [];
      const next: EmailInboxForm[] = rows.map((row) => ({
        ...row,
        imap: { ...row.imap, password: '' },
      }));
      setEmailInboxes(next);
      setEmailSubjectRules(response.subjectRules || []);
      setEmailSubjectRuleInputs(
        (response.subjectRules || []).map((rule) => (rule.keywords || []).join(', '))
      );
      setEmailInboxesLoaded(true);
    } catch (e: any) {
      console.error('Failed to fetch email inbox settings', e);
      setError(normalizeErrorMessage(e) || 'Failed to load email inbox settings');
      setEmailInboxes([]);
    } finally {
      setEmailInboxesLoading(false);
    }
  };

  const saveEmailInboxSettings = async () => {
    setEmailInboxesSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const parseSubjectKeywords = (value: string) =>
        String(value || '')
          .split(/\s+OR\s+|,|\n|;|\|/gi)
          .map((k) => k.trim())
          .filter((k) => k.length > 0);

      const subjectRules = emailSubjectRules.map((rule, idx) => ({
        category: rule.category,
        keywords: parseSubjectKeywords(emailSubjectRuleInputs[idx] || ''),
      }));

      const payload = {
        inboxes: emailInboxes.map((row) => ({
          id: safeTrim(row.id),
          name: safeTrim(row.name),
          enabled: !!row.enabled,
          provider: 'imap' as const,
          mailbox: safeTrim(row.mailbox) || 'INBOX',
          types: (row.types || []) as EmailInboxType[],
          imap: {
            host: safeTrim(row.imap.host),
            port: Number(row.imap.port) || 993,
            secure: row.imap.secure !== false,
            user: safeTrim(row.imap.user),
            ...(row.imap.password ? { password: row.imap.password } : {}),
          },
        })),
        subjectRules,
      };

      const saved = await updateEmailInboxes(payload);
      const next: EmailInboxForm[] = (saved?.inboxes || []).map((row) => ({
        ...row,
        imap: { ...row.imap, password: '' },
      }));
      setEmailInboxes(next);
      setEmailSubjectRules(saved?.subjectRules || []);
      setEmailSubjectRuleInputs(
        (saved?.subjectRules || []).map((rule) => (rule.keywords || []).join(', '))
      );
      setSuccessMsg('Email inbox settings saved.');
    } catch (e: any) {
      console.error('Failed to save email inbox settings', e);
      setError(normalizeErrorMessage(e) || 'Failed to save email inbox settings');
    } finally {
      setEmailInboxesSaving(false);
    }
  };

  const addSubjectRule = () => {
    setEmailSubjectRules((prev) => [
      ...prev,
      { category: 'rfq', keywords: [] },
    ]);
    setEmailSubjectRuleInputs((prev) => [...prev, '']);
  };

  const removeSubjectRule = (index: number) => {
    setEmailSubjectRules((prev) => prev.filter((_, i) => i !== index));
    setEmailSubjectRuleInputs((prev) => prev.filter((_, i) => i !== index));
  };


  const fetchPortalSettings = async () => {
    setPortalLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const res: any = await get<any>('/portal-automation-settings');

      const rows: PortalAutomationSetting[] = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.settings)
            ? res.settings
            : [];

      setPortalRows(rows);
    } catch (e: any) {
      console.error('Failed to fetch portal automation settings', e);
      setError(normalizeErrorMessage(e) || 'Failed to load portal settings');
      setPortalRows([]);
    } finally {
      setPortalLoading(false);
    }
  };

  const openCreatePortalModal = () => {
    setPortalModalMode('create');
    setPortalForm(defaultPortalForm);
    setPortalModalOpen(true);
  };

  const openEditPortalModal = (row: PortalAutomationSetting) => {
    setPortalModalMode('edit');

    const otp = row.oneTimePatterns || {
      usernameRegex: null,
      passwordRegex: null,
      otpRegex: null,
    };

    setPortalForm({
      _id: row._id,
      matchType: row.matchType,
      matchValue: row.matchValue || '',
      portalType: (row.portalType as PortalType) || 'procureship',

      credentialsMode: (row.credentialsMode as CredentialsMode) || 'static',
      portalUrlOverride: row.portalUrlOverride || '',
      oneTimePatterns: {
        usernameRegex: otp.usernameRegex || '',
        passwordRegex: otp.passwordRegex || '',
        otpRegex: otp.otpRegex || '',
      },

      username: row.username || '',
      password: '', // never prefill
      enabled: !!row.enabled,
      notes: row.notes || '',
    });

    setPortalModalOpen(true);
  };

  const closePortalModal = () => {
    setPortalModalOpen(false);
  };

  const closeTestModal = () => {
    setTestModalOpen(false);
    setTestResult(null);
    setTestError(null);
  };

  const savePortalSetting = async () => {
    setPortalSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      if (!safeTrim(portalForm.matchValue)) {
        setError('Match value is required (email/domain).');
        setPortalSaving(false);
        return;
      }
      if (!portalForm.portalType) {
        setError('Portal type is required.');
        setPortalSaving(false);
        return;
      }

      const payload: any = {
        matchType: portalForm.matchType,
        matchValue: safeLower(portalForm.matchValue),
        portalType: safeLower(portalForm.portalType),

        credentialsMode: portalForm.credentialsMode,
        portalUrlOverride: safeTrim(portalForm.portalUrlOverride) || null,
        oneTimePatterns: {
          usernameRegex: normalizeRegexInput(portalForm.oneTimePatterns.usernameRegex),
          passwordRegex: normalizeRegexInput(portalForm.oneTimePatterns.passwordRegex),
          otpRegex: normalizeRegexInput(portalForm.oneTimePatterns.otpRegex),
        },

        username: safeTrim(portalForm.username) || null,
        enabled: !!portalForm.enabled,
        notes: safeTrim(portalForm.notes) || null,
      };

      if (portalForm.password && safeTrim(portalForm.password).length > 0) {
        payload.password = portalForm.password;
      }

      if (portalModalMode === 'create') {
        await postJson('/portal-automation-settings', payload);
        setSuccessMsg('Portal automation setting created.');
      } else {
        if (!portalForm._id) throw new Error('Missing setting id.');
        await patch(`/portal-automation-settings/${portalForm._id}`, payload);
        setSuccessMsg('Portal automation setting updated.');
      }

      closePortalModal();
      await fetchPortalSettings();
    } catch (e: any) {
      console.error('Failed to save portal automation setting', e);
      setError(normalizeErrorMessage(e) || 'Failed to save portal setting');
    } finally {
      setPortalSaving(false);
    }
  };

  const togglePortalSetting = async (row: PortalAutomationSetting) => {
    setPortalSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      await patch(`/portal-automation-settings/${row._id}/toggle`, {});
      setSuccessMsg(`Portal automation setting ${row.enabled ? 'disabled' : 'enabled'}.`);
      await fetchPortalSettings();
    } catch (e: any) {
      console.error('Failed to toggle portal automation setting', e);
      setError(normalizeErrorMessage(e) || 'Failed to toggle setting');
    } finally {
      setPortalSaving(false);
    }
  };

  const testRunPortalSetting = async (row: PortalAutomationSetting) => {
    setError(null);
    setSuccessMsg(null);
    setTestError(null);
    setTestResult(null);
    setTestModalOpen(true);
    setTestRunningId(row._id);

    try {
      const res: any = await postJson(`/portal-automation-settings/${row._id}/test-run`, {
        mode: 'create_rfq',
      });

      const payload: TestRunResult = res?.data ?? res;

      setTestResult(payload);
      setSuccessMsg(`Test Run completed for ${row.portalType}.`);
      await fetchPortalSettings();
    } catch (e: any) {
      console.error('Test Run failed', e);
      const msg = normalizeErrorMessage(e) || 'Test Run failed';
      setTestError(msg);
      setError(msg);
      await fetchPortalSettings();
    } finally {
      setTestRunningId(null);
    }
  };

  const onCopyTestResult = async () => {
    try {
      const text = JSON.stringify({ result: testResult, error: testError }, null, 2);
      await navigator.clipboard.writeText(text);
      setSuccessMsg('Copied test result to clipboard.');
    } catch {
      setError('Failed to copy test result.');
    }
  };

  // Initial load
  useEffect(() => {
    fetchPriceListSettings();
  }, []);

  // Lazy-load tab content
  useEffect(() => {
    if (activeTab === 'portalAutomation' && portalRows.length === 0) {
      fetchPortalSettings();
    }
    if (activeTab === 'exchangeRate' && !lastUpdated) {
      fetchExchangeRate();
    }
    if (activeTab === 'exchangeRate' && !vatRateLoaded && !vatRateLoading) {
      fetchVatRate();
      fetchCompetitorWarn();
    }
    if (activeTab === 'rfqProcessing' && !ifsLoaded && !ifsLoading) {
      fetchIfsSettings();
    }
    if (activeTab === 'emailInboxes' && !emailInboxesLoaded && !emailInboxesLoading) {
      fetchEmailInboxSettings();
    }
    if (activeTab === 'itemCategories' && !categoriesLoaded && !categoriesLoading) {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const onRefresh = async () => {
    if (activeTab === 'itemCategories') return fetchCategories();
    if (activeTab === 'creditReasons') {
      setCreditReasonsReload((n) => n + 1);
      return;
    }
    if (activeTab === 'priceList') return fetchPriceListSettings();
    if (activeTab === 'exchangeRate') {
      fetchVatRate();
      fetchCompetitorWarn();
      return fetchExchangeRate();
    }
    if (activeTab === 'rfqProcessing') return fetchIfsSettings();
    if (activeTab === 'emailInboxes') {
      return fetchEmailInboxSettings();
    }
    return fetchPortalSettings();
  };

  const onSave = async () => {
    if (activeTab === 'priceList') return onSavePriceListSettings();
    if (activeTab === 'exchangeRate') return saveExchangeRate();
    if (activeTab === 'rfqProcessing') return saveIfsSettings();
    if (activeTab === 'emailInboxes') {
      return saveEmailInboxSettings();
    }
    return;
  };

  const headerSaveDisabled =
    activeTab === 'priceList'
      ? loading || saving || !rawJsonIsValid
      : activeTab === 'exchangeRate'
        ? exchangeRateLoading || exchangeRateSaving || usdToTtd <= 0
        : activeTab === 'rfqProcessing'
          ? ifsLoading || ifsSaving
      : activeTab === 'emailInboxes'
        ? emailInboxesLoading || emailInboxesSaving
        : true;

  const headerRefreshDisabled =
    loading ||
    saving ||
    portalLoading ||
    portalSaving ||
    exchangeRateLoading ||
    exchangeRateSaving ||
    vatRateLoading ||
    vatRateSaving ||
    ifsLoading ||
    ifsSaving ||
    emailInboxesLoading ||
    emailInboxesSaving ||
    categoriesLoading ||
    categorySaving ||
    !!testRunningId;

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      {/* The app's one header band: same gutter, same 20px title, same control
          heights as every other module (docs/DESIGN.md). Currency Conversion —
          this page's Exchange Rate tab under its own URL — uses the identical
          band, so switching between the two does not move the title. */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Settings</h1>
            <ModuleInfoButton content={SETTINGS_INFO_CONTENT} ariaLabel="Settings information" />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={onRefresh}
              disabled={headerRefreshDisabled}
              loading={
                loading || portalLoading || exchangeRateLoading || vatRateLoading || ifsLoading || categoriesLoading
              }
            >
              Refresh
            </Button>

            {/* Header Save for Price List and Exchange Rate tabs */}
            {(activeTab === 'priceList' ||
              activeTab === 'exchangeRate' ||
              activeTab === 'rfqProcessing' ||
              activeTab === 'emailInboxes') && (
              <Button
                variant="primary"
                onClick={onSave}
                disabled={headerSaveDisabled}
                loading={saving || exchangeRateSaving || ifsSaving}
                title={
                  activeTab === 'priceList' && !rawJsonIsValid
                    ? 'Fix JSON before saving'
                    : activeTab === 'exchangeRate' && usdToTtd <= 0
                      ? 'Exchange rate must be > 0'
                      : activeTab === 'rfqProcessing' && (ifsLoading || ifsSaving)
                        ? 'Settings are loading or saving'
                        : 'Save settings'
                }
              >
                {saving || exchangeRateSaving || ifsSaving ? 'Saving...' : 'Save'}
              </Button>
            )}

            {/* Add mapping button for Portal tab. Never on screen at the same
                time as Save above, so the screen keeps one primary action. */}
            {activeTab === 'portalAutomation' && (
              <Button
                variant="primary"
                onClick={openCreatePortalModal}
                disabled={portalLoading || portalSaving || !!testRunningId}
                icon={<Plus aria-hidden="true" className="h-4 w-4" />}
              >
                Add mapping
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3">
          <SegmentedControl
            aria-label="Settings section"
            segments={SETTINGS_TABS}
            value={activeTab}
            onChange={setActiveTab}
          />
        </div>

        <div className="mt-2 text-xs text-slate-600">
          {activeTab === 'priceList' ? (
            <>Vendor / Price List settings (used by price list ingestion & matching).</>
          ) : activeTab === 'exchangeRate' ? (
            <>Configure the fixed TTD to USD exchange rate used in all quotations.</>
          ) : activeTab === 'rfqProcessing' ? (
            <>Configure RFQ extraction rules for IFS order sheets with missing prices.</>
          ) : activeTab === 'emailInboxes' ? (
            <>Configure multiple email inboxes and route each inbox to the right workflow.</>
          ) : activeTab === 'itemCategories' ? (
            <>The list a catalogue item can be filed under — used to group a price list and to split an order by the department that pays for it.</>
          ) : activeTab === 'creditReasons' ? (
            <>The reasons a credit note or a vendor return can be given for.</>
          ) : (
            <>Automation mapping: sender / domain → portal client → credentials mode → optional URL override.</>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden px-4 py-4 sm:px-8">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm h-full overflow-hidden flex flex-col">
          {/* Alerts. A failure offers the retry rather than sitting as a line
              of red prose over a panel that may simply be empty. */}
          {(error || successMsg) && (
            <div className="px-6 py-3 border-b border-slate-200">
              {error && <ErrorState message={error} onRetry={onRefresh} />}
              {successMsg && (
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                  {successMsg}
                </div>
              )}
            </div>
          )}

          {/* Credit Note Reasons Tab (D44) — the whole screen lives in its feature folder. */}
          {activeTab === 'creditReasons' && <CreditReasonsSettings reloadSignal={creditReasonsReload} />}

          {/* Item Categories Tab */}
          {activeTab === 'itemCategories' && (
            <div className="p-6 flex-1 overflow-auto">
              <div className="max-w-2xl">
                <div className="text-sm font-semibold text-slate-800">Item categories</div>
                <p className="text-xs text-slate-500 mt-1 mb-4">
                  The list the Category field on a catalogue item offers. Six departments ship
                  with the system and cannot be removed; anything you add here is available
                  immediately, with no deploy. Removing one never re-categorises the items
                  already filed under it.
                </p>

                <div className="flex gap-2 mb-5">
                  <input
                    value={newCategoryLabel}
                    onChange={(e) => setNewCategoryLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onAddCategory();
                    }}
                    placeholder="New category, e.g. Cold Cuts"
                    disabled={categorySaving}
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  />
                  <Button
                    variant="primary"
                    onClick={onAddCategory}
                    disabled={!newCategoryLabel.trim()}
                    loading={categorySaving}
                  >
                    {categorySaving ? 'Saving...' : 'Add'}
                  </Button>
                </div>

                {categoriesLoading ? (
                  <div className="text-sm text-slate-500">Loading categories...</div>
                ) : (
                  <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                    {categories.map((cat) => (
                      <div key={cat.key} className="flex items-center gap-3 px-4 py-2.5">
                        {editingCategoryId && editingCategoryId === cat._id ? (
                          <>
                            <input
                              value={editingCategoryLabel}
                              onChange={(e) => setEditingCategoryLabel(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && cat._id) onRenameCategory(cat._id);
                                if (e.key === 'Escape') setEditingCategoryId(null);
                              }}
                              autoFocus
                              className="flex-1 px-2 py-1 border border-slate-300 rounded-md text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                            />
                            <Button
                              size="sm"
                              onClick={() => cat._id && onRenameCategory(cat._id)}
                              disabled={categorySaving || !editingCategoryLabel.trim()}
                            >
                              Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingCategoryId(null)}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-sm text-slate-800">{cat.label}</span>
                            {/* The STORED value. Shown because it is what lands
                                on every row and what a filter matches on — the
                                thing that used to have six spellings. */}
                            <code className="text-xs text-slate-400">{cat.key}</code>
                            {cat.builtIn ? (
                              <span className="text-xs text-slate-400 px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200">
                                built in
                              </span>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingCategoryId(cat._id);
                                    setEditingCategoryLabel(cat.label);
                                  }}
                                  disabled={categorySaving}
                                >
                                  Rename
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => onDeleteCategory(cat)}
                                  disabled={categorySaving}
                                >
                                  Remove
                                </Button>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Price List Settings Tab */}
          {activeTab === 'priceList' && (
            <div className="p-6 flex-1 overflow-auto">
              <div className="mb-3 flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-slate-800">Price list settings (JSON)</div>
                  <div className="text-xs text-slate-500">
                    Schema-agnostic editor. Format/minify/copy. Save is disabled when JSON is invalid.
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <StatusBadge
                    status={rawJsonIsValid ? 'Valid JSON' : 'Invalid JSON'}
                    tone={rawJsonIsValid ? 'positive' : 'negative'}
                  />

                  <span className="text-xs text-slate-500">{rawJsonCharCount} chars</span>

                  <Button size="sm" onClick={onFormatJson} disabled={loading || saving}>
                    Format
                  </Button>

                  <Button size="sm" onClick={onMinifyJson} disabled={loading || saving}>
                    Minify
                  </Button>

                  <Button size="sm" onClick={onCopyJson} disabled={loading || saving}>
                    Copy
                  </Button>

                  <Button
                    size="sm"
                    onClick={onResetToLastLoaded}
                    disabled={loading || saving}
                    title="Revert changes to last loaded state"
                  >
                    Reset
                  </Button>
                </div>
              </div>

              <textarea
                value={rawJson}
                onChange={(e) => setRawJson(e.target.value)}
                spellCheck={false}
                className={[
                  'w-full min-h-[520px] font-mono text-xs border rounded-md p-3 focus:ring-2',
                  rawJsonIsValid
                    ? 'border-slate-200 focus:ring-primary-500/30'
                    : 'border-red-300 focus:ring-red-200',
                ].join(' ')}
              />

              {!rawJsonIsValid && (
                <div className="mt-2 text-xs text-red-700">
                  JSON is invalid — please fix before saving. Tip: click <b>Format</b> once it’s valid.
                </div>
              )}
            </div>
          )}

          {/* Exchange Rate Tab */}
          {activeTab === 'exchangeRate' && (
            <div className="p-6 flex-1 overflow-auto">
              <div className="max-w-2xl mx-auto">
                <div className="mb-6">
                  <div className="text-sm font-semibold text-slate-800 mb-1">TTD to USD exchange rate</div>
                  <div className="text-xs text-slate-500">
                    Set the fixed exchange rate used for converting USD prices to TTD in all quotations.
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* USD to TTD Input */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Exchange rate (1 USD = ? TTD)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm">
                          $1 =
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={usdToTtd}
                          onChange={(e) => setUsdToTtd(parseFloat(e.target.value) || 0)}
                          className="w-full pl-12 pr-16 py-3 border border-slate-300 rounded-md text-lg font-semibold text-slate-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                          placeholder="6.00"
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                          TT$
                        </span>
                      </div>
                      {!lastUpdated && (
                        <div className="mt-2 text-xs text-slate-500">Not set. Every TT$/US$ conversion is refused until a rate is saved.</div>
                      )}
                    </div>

                    {/* TTD to USD Display (calculated) */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Inverse rate (1 TTD = ? USD)
                      </label>
                      <div className="w-full px-4 py-3 bg-white border border-slate-200 rounded-md">
                        <div className="text-lg font-semibold text-slate-900">
                          TT$1 = ${usdToTtd > 0 ? (1 / usdToTtd).toFixed(4) : '0.0000'} USD
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-slate-500">Auto-calculated from above rate</div>
                    </div>
                  </div>

                  {/* Reason for the change — stored on the change log entry */}
                  <div className="mt-6">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Reason for change <span className="font-normal text-slate-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={rateNote}
                      onChange={(e) => setRateNote(e.target.value)}
                      maxLength={200}
                      placeholder="e.g. Central Bank mid-rate update for August"
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm text-slate-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    />
                    <div className="mt-1 text-xs text-slate-500">
                      Saved against this change in the log below.
                    </div>
                  </div>

                  {/* Example Conversion */}
                  <div className="mt-6 p-4 bg-primary-50 border border-primary-500/20 rounded-md">
                    <div className="text-xs font-semibold text-slate-900 mb-2">Example conversion:</div>
                    <div className="text-sm text-slate-700">
                      <div className="mb-1">$100 USD = TT${(100 * usdToTtd).toFixed(2)}</div>
                      <div>TT$100 = ${usdToTtd > 0 ? (100 / usdToTtd).toFixed(2) : '0.00'} USD</div>
                    </div>
                  </div>
                </div>

                {/* Metadata */}
                {lastUpdated && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Last updated</div>
                        <div className="text-slate-900">{formatDt(lastUpdated)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Updated by</div>
                        <div className="text-slate-900">{updatedBy || 'System'}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Change Log */}
                <div className="bg-white border border-slate-200 rounded-lg mb-6">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                    <div>
                      <div className="text-sm font-semibold text-slate-800">Rate change log</div>
                      <div className="text-xs text-slate-500">
                        Every change, with the period it applied to.
                      </div>
                    </div>
                    <Button size="sm" onClick={fetchRateHistory} loading={rateHistoryLoading}>
                      {rateHistoryLoading ? 'Loading...' : 'Refresh'}
                    </Button>
                  </div>

                  {rateHistory.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-slate-500 text-center">
                      {rateHistoryLoading
                        ? 'Loading change log...'
                        : 'No rate changes recorded yet. The next change you save will appear here.'}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      {/* `data-table` (index.css) draws the header, the cells and
                          the row hover — the same table as every other module. */}
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Rate (1 USD =)</th>
                            <th>Effective from</th>
                            <th>Effective to</th>
                            <th>Changed by</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rateHistory.map((h) => (
                            <tr key={h._id}>
                              <td className="font-semibold text-slate-900 whitespace-nowrap tabular-nums">
                                TT${h.usdToTtd.toFixed(4)}
                                {h.isActive && (
                                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    Current
                                  </span>
                                )}
                              </td>
                              <td className="whitespace-nowrap">{formatDt(h.effectiveFrom)}</td>
                              <td className="whitespace-nowrap">
                                {h.effectiveTo ? formatDt(h.effectiveTo) : '—'}
                              </td>
                              <td>{h.setByName}</td>
                              <td className="text-slate-500">{h.notes || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Info Box */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info aria-hidden="true" className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                    <div>
                      <div className="text-sm font-semibold text-amber-900 mb-1">Important notes:</div>
                      <ul className="text-xs text-amber-800 space-y-1 list-disc list-inside">
                        <li>
                          A rate change applies to <strong>quotations generated after the change only</strong>
                        </li>
                        <li>
                          Existing quotations, confirmed orders and their purchase orders keep the rate they
                          were created with — reopening or re-exporting them will not re-price them
                        </li>
                        <li>Every change is recorded in the change log above and in the audit trail</li>
                        <li>The external exchange rate API is kept in the codebase for potential future use</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* ── VAT rate (owner decision 2026-09-24, "one Trinidad VAT
                    rate") — a separate setting beside the exchange rate
                    above, own edit and save. The government can change VAT,
                    so this is the ONLY place a Trinidad VAT percentage is
                    ever typed; every VAT-applicable Master Catalogue row's
                    cost is grossed at this rate, and nothing else. */}
                <div className="mt-10 mb-6">
                  <div className="text-sm font-semibold text-slate-800 mb-1">VAT rate</div>
                  <div className="text-xs text-slate-500">
                    The one Trinidad VAT rate the whole app costs a VAT-applicable Master Catalogue
                    item at. It is never charged to a vessel — vessels are never charged VAT.
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">VAT rate (%)</label>
                  <div className="relative max-w-xs">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="99.99"
                      value={vatRateInput}
                      onChange={(e) => setVatRateInput(e.target.value)}
                      className="w-full pl-4 pr-12 py-3 border border-slate-300 rounded-md text-lg font-semibold text-slate-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                      placeholder="VAT rate"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                      %
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    {vatRatePercent != null
                      ? `Current rate: ${vatRatePercent}%.`
                      : vatRateLoading
                        ? 'Loading current rate…'
                        : 'No VAT rate has been set yet — VAT-applicable catalogue items cannot be costed until one is.'}
                  </div>

                  <div className="mt-4">
                    <Button variant="primary" onClick={saveVatRate} loading={vatRateSaving} disabled={vatRateLoading || vatRateSaving}>
                      {vatRateSaving ? 'Saving...' : 'Save VAT rate'}
                    </Button>
                    {vatRestatedMsg && (
                      <span className="ml-3 text-sm text-emerald-700">{vatRestatedMsg}</span>
                    )}
                  </div>
                </div>

                {/* ── Competitor markup warning (D97): the quotation grid's Competitor Markup % column
                    turns red under this value. Server default is 20 until someone sets it. */}
                <div className="mt-10 mb-6">
                  <div className="text-sm font-semibold text-slate-800 mb-1">Competitor markup warning</div>
                  <div className="text-xs text-slate-500">
                    On the quotation grid, a line whose competitor price marks up our cost by less than this
                    percentage is highlighted red.
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Warn below (% markup on cost)</label>
                  <div className="relative max-w-xs">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1000"
                      value={warnInput}
                      onChange={(e) => setWarnInput(e.target.value)}
                      className="w-full pl-4 pr-12 py-3 border border-slate-300 rounded-md text-lg font-semibold text-slate-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                      placeholder="Competitor markup warning"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                      %
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    {warnPercent != null
                      ? `Current threshold: ${warnPercent}%.`
                      : warnLoading
                        ? 'Loading current threshold…'
                        : 'Threshold not loaded.'}
                  </div>
                  <div className="mt-4">
                    <Button variant="primary" onClick={saveCompetitorWarn} loading={warnSaving} disabled={warnLoading || warnSaving}>
                      {warnSaving ? 'Saving...' : 'Save threshold'}
                    </Button>
                  </div>
                </div>

                {/* Metadata */}
                {vatRateUpdatedAt && (
                  <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Last updated</div>
                        <div className="text-slate-900">{formatDt(vatRateUpdatedAt)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Updated by</div>
                        <div className="text-slate-900">{vatRateUpdatedBy || 'System'}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* VAT rate change log */}
                <div className="bg-white border border-slate-200 rounded-lg mb-6">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                    <div>
                      <div className="text-sm font-semibold text-slate-800">VAT rate change log</div>
                      <div className="text-xs text-slate-500">Every change, newest first.</div>
                    </div>
                    <Button size="sm" onClick={fetchVatRateHistory} loading={vatRateHistoryLoading}>
                      {vatRateHistoryLoading ? 'Loading...' : 'Refresh'}
                    </Button>
                  </div>

                  {vatRateHistory.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-slate-500 text-center">
                      {vatRateHistoryLoading
                        ? 'Loading change log...'
                        : 'No VAT rate changes recorded yet. The next change you save will appear here.'}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Rate</th>
                            <th>Previous rate</th>
                            <th>Changed at</th>
                            <th>Changed by</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vatRateHistory.map((h, i) => (
                            <tr key={`${h.changedAt}-${i}`}>
                              <td className="font-semibold text-slate-900 whitespace-nowrap tabular-nums">
                                {h.ratePercent}%
                              </td>
                              <td className="whitespace-nowrap tabular-nums">
                                {h.previousRatePercent != null ? `${h.previousRatePercent}%` : '—'}
                              </td>
                              <td className="whitespace-nowrap">{formatDt(h.changedAt)}</td>
                              <td>{h.changedBy || 'System'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* keep state used */}
                <div className="hidden">{exchangeRate}</div>
              </div>
            </div>
          )}

          {/* RFQ Processing Tab */}
          {activeTab === 'rfqProcessing' && (
            <div className="p-6 flex-1 overflow-auto">
              <div className="max-w-3xl mx-auto">
                <div className="mb-6">
                  <div className="text-sm font-semibold text-slate-800 mb-1">IFS RFQ processing</div>
                  <div className="text-xs text-slate-500">
                    Create RFQs only for items missing prices while still generating quotations for all items.
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 space-y-5">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={ifsSettings.enabled}
                      onChange={(e) =>
                        setIfsSettings((prev) => ({ ...prev, enabled: e.target.checked }))
                      }
                      className="h-4 w-4 accent-primary-500"
                    />
                    <span className="text-sm font-semibold text-slate-700">Enable IFS RFQ flow</span>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Match from email domains
                    </label>
                    <textarea
                      value={ifsDomainsText}
                      onChange={(e) => setIfsDomainsText(e.target.value)}
                      placeholder={'ifs.example.com\nifsclient.com'}
                      className="w-full min-h-[80px] border border-slate-300 rounded-md px-3 py-2 text-xs font-mono focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    />
                    <div className="mt-1 text-xs text-slate-500">
                      One domain per line. Email sender domain must match any entry.
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Match subject keywords
                    </label>
                    <textarea
                      value={ifsSubjectText}
                      onChange={(e) => setIfsSubjectText(e.target.value)}
                      placeholder={'ifs order\norder sheet\nprice list'}
                      className="w-full min-h-[80px] border border-slate-300 rounded-md px-3 py-2 text-xs font-mono focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    />
                    <div className="mt-1 text-xs text-slate-500">
                      Optional. If provided, subject must include any keyword.
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Price column keywords
                    </label>
                    <textarea
                      value={ifsPriceKeysText}
                      onChange={(e) => setIfsPriceKeysText(e.target.value)}
                      placeholder={'price\nunit price\nrate\namount'}
                      className="w-full min-h-[80px] border border-slate-300 rounded-md px-3 py-2 text-xs font-mono focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    />
                    <div className="mt-1 text-xs text-slate-500">
                      Used to detect the price column in IFS order sheets.
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={ifsSettings.treatZeroAsMissing}
                      onChange={(e) =>
                        setIfsSettings((prev) => ({
                          ...prev,
                          treatZeroAsMissing: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 accent-primary-500"
                    />
                    <span className="text-sm text-slate-700">Treat price = 0 as missing</span>
                  </div>
                </div>

                <div className="mt-4 text-xs text-slate-500">
                  Tip: You can match using domains, subject keywords, or both. If both are empty, the flow is disabled.
                </div>
              </div>
            </div>
          )}

          {/* Email Inboxes Tab */}
          {activeTab === 'emailInboxes' && (
            <div className="p-6 flex-1 overflow-auto">
              <div className="mb-4">
                <div className="text-sm font-semibold text-slate-800">Email inboxes</div>
                <div className="text-xs text-slate-500">
                  Which subject will go to which email category.
                </div>
              </div>

              <div className="border border-slate-200 rounded-md p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Subject rules</div>
                    {/* D43.3: a rule no longer files an email by itself. */}
                    <p className="text-xs text-slate-500 mt-0.5">
                      A hint for the automatic sorting, which reads the whole email and decides the tab. Keywords match whole words.
                    </p>
                  </div>
                  {/* Secondary: the header Save above is this screen's one
                      primary action while the Email Inboxes tab is open. */}
                  <Button
                    size="sm"
                    onClick={addSubjectRule}
                    icon={<Plus aria-hidden="true" className="h-4 w-4" />}
                  >
                    Add rule
                  </Button>
                </div>

                {emailInboxesLoading ? (
                  <div className="text-sm text-slate-500">Loading rules...</div>
                ) : emailSubjectRules.length === 0 ? (
                  <div className="text-sm text-slate-500">
                    No subject rules yet. Click <b>Add rule</b> to create one.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {emailSubjectRules.map((rule, idx) => (
                      <div key={`${rule.category}-${idx}`} className="border border-slate-200 rounded-md p-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                          <div className="md:col-span-2">
                            <label className="block text-xs font-semibold text-slate-700 mb-1">
                              Subject keywords
                            </label>
                            <textarea
                              rows={2}
                              value={emailSubjectRuleInputs[idx] ?? (rule.keywords || []).join(', ')}
                              onChange={(e) => {
                                const value = e.target.value;
                                setEmailSubjectRuleInputs((prev) =>
                                  prev.map((v, i) => (i === idx ? value : v))
                                );
                              }}
                              placeholder="e.g. purchase order, order confirmation, rfq request"
                              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 resize-none"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-slate-700 mb-1">Category</label>
                            <select
                              value={rule.category}
                              onChange={(e) =>
                                setEmailSubjectRules((prev) =>
                                  prev.map((r, i) =>
                                    i === idx
                                      ? { ...r, category: e.target.value as EmailInboxType }
                                      : r
                                  )
                                )
                              }
                              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                            >
                              {inboxTypeOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="mt-3 flex justify-end">
                          <Button size="sm" variant="danger" onClick={() => removeSubjectRule(idx)}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-3 text-xs text-slate-500">
                  Tip: Match phrases or words. Separate with comma, newline, “OR”, or “|”.
                </div>
              </div>
            </div>
          )}

          {/* Portal Automation Tab */}
          {activeTab === 'portalAutomation' && (
            <div className="p-6 flex-1 overflow-auto">
              <div className="mb-4">
                <div className="text-sm font-semibold text-slate-800">Portal automation mappings</div>
                <div className="text-xs text-slate-500">
                  Rules decide: matching, then portal client, then credentials mode, then an
                  optional portal URL override, then one-time patterns.
                </div>
              </div>

              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                  <div className="text-xs text-slate-600">
                    {portalLoading ? 'Loading mappings…' : `${portalRows.length} mapping(s)`}
                  </div>
                  <div className="text-xs text-slate-500">Password is never shown. Update it via Edit.</div>
                </div>

                <div className="overflow-auto">
                  {/* `data-table` (index.css) draws the header, the cells and
                      the row hover — the same table as every other module. */}
                  <table className="data-table min-w-[1180px]">
                    <thead className="sticky top-0 z-10">
                      <tr>
                        <th>Enabled</th>
                        <th>Match</th>
                        <th>Value</th>
                        <th>Portal</th>
                        <th>Cred mode</th>
                        <th>URL override</th>
                        <th>Username</th>
                        <th>Password</th>
                        <th>Last success</th>
                        <th>Last error</th>
                        <th>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {portalRows.length === 0 && !portalLoading ? (
                        <tr>
                          <td colSpan={11} className="text-slate-500">
                            No portal mappings yet. Click <b>Add mapping</b> to configure.
                          </td>
                        </tr>
                      ) : (
                        portalRows.map((row) => (
                          <tr key={row._id}>
                            <td>
                              {/* A switched-off mapping is a choice, not a fault: neutral, not red. */}
                              <StatusBadge
                                active={row.enabled}
                                activeLabel="Enabled"
                                inactiveLabel="Disabled"
                                tone={row.enabled ? 'positive' : 'neutral'}
                              />
                            </td>

                            <td>{row.matchType}</td>
                            <td className="text-slate-900 font-mono">{row.matchValue}</td>

                            <td>{row.portalType}</td>

                            <td>{row.credentialsMode || 'static'}</td>

                            <td>
                              {row.portalUrlOverride ? (
                                <span className="font-mono text-xs" title={row.portalUrlOverride}>
                                  {row.portalUrlOverride.length > 34
                                    ? row.portalUrlOverride.slice(0, 34) + '…'
                                    : row.portalUrlOverride}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>

                            <td>{row.username || '—'}</td>

                            <td>
                              <StatusBadge
                                status={row.hasPassword ? 'Set' : 'Not set'}
                                tone={row.hasPassword ? 'positive' : 'attention'}
                              />
                            </td>

                            <td className="whitespace-nowrap">{formatDt(row.lastSuccessAt)}</td>

                            <td>
                              {row.lastErrorAt ? (
                                <div>
                                  <div className="text-red-700">{formatDt(row.lastErrorAt)}</div>
                                  <div className="text-red-600 max-w-[260px] truncate" title={row.lastError || ''}>
                                    {row.lastError || '—'}
                                  </div>
                                </div>
                              ) : (
                                '—'
                              )}
                            </td>

                            <td>
                              {/* All secondary: "Add mapping" in the header band
                                  is this tab's one primary action, and a row of
                                  filled buttons per row read as eleven of them. */}
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => openEditPortalModal(row)}
                                  disabled={portalSaving || !!testRunningId}
                                >
                                  Edit
                                </Button>

                                <Button
                                  size="sm"
                                  onClick={() => togglePortalSetting(row)}
                                  disabled={portalSaving || !!testRunningId}
                                >
                                  {row.enabled ? 'Disable' : 'Enable'}
                                </Button>

                                <Button
                                  size="sm"
                                  onClick={() => testRunPortalSetting(row)}
                                  disabled={portalSaving || !!testRunningId || !row.enabled}
                                  loading={testRunningId === row._id}
                                  title={!row.enabled ? 'Enable mapping first to test.' : 'Run this mapping once now.'}
                                >
                                  {testRunningId === row._id ? 'Testing…' : 'Test run'}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Help / notes */}
              <div className="mt-4 text-xs text-slate-500">
                <div className="mb-1">
                  <b>Recommended matching priority:</b> fromEmail, then fromDomain, then portalDomain.
                </div>
                <div className="mb-1">
                  <b>One-time portals:</b> Set <b>Cred mode = one_time_email</b> and provide regex patterns if needed.
                </div>
                <div className="mb-1">
                  <b>URL override:</b> Use when email link is unreliable or you want a fixed landing URL per client.
                </div>
                <div className="mb-1">
                  <b>Test run:</b> For <b>one_time_email</b> mappings, test run needs real email context (use PortalFetcher
                  with a real email).
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ✅ Test Result Modal — the app's Modal, not a hand-built overlay, so it
          portals, closes on Escape and announces itself as a dialog. */}
      <Modal
        isOpen={testModalOpen}
        title="Test run result"
        onClose={closeTestModal}
        maxWidthClass="max-w-[860px]"
      >
            <div>
              <div className="mb-3 text-xs text-slate-600">
                Summary only. Detailed Playwright logs are printed in backend console.
              </div>
              {testRunningId && (
                <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                  Running test… please check backend console for live Playwright logs.
                </div>
              )}

              {testError && (
                <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {testError}
                </div>
              )}

              {testResult && (
                <div className="mt-3 border border-slate-200 rounded-md overflow-hidden">
                  <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 text-xs text-slate-700">
                    {testResult.portalType} • mode={testResult.mode} • duration={testResult.durationMs}ms
                  </div>

                  <div className="p-4 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="text-xs text-slate-600">
                        <div className="font-semibold text-slate-700 mb-1">Credentials source</div>
                        <div className="font-mono">{testResult.credsSource || '—'}</div>
                      </div>

                      <div className="text-xs text-slate-600">
                        <div className="font-semibold text-slate-700 mb-1">Effective URL</div>
                        <div className="font-mono break-all">{testResult.effectiveUrl || '—'}</div>
                      </div>

                      <div className="text-xs text-slate-600">
                        <div className="font-semibold text-slate-700 mb-1">Downloaded</div>
                        <div>
                          <StatusBadge
                            status={testResult.downloaded ? 'Yes' : 'No'}
                            tone={testResult.downloaded ? 'positive' : 'attention'}
                          />
                        </div>
                      </div>

                      <div className="text-xs text-slate-600">
                        <div className="font-semibold text-slate-700 mb-1">File</div>
                        <div className="font-mono">
                          {testResult.filename || '—'} {testResult.bytes ? `(${testResult.bytes} bytes)` : ''}
                        </div>
                        <div className="text-xs text-slate-500">{testResult.mimeType || ''}</div>
                      </div>

                      <div className="text-xs text-slate-600">
                        <div className="font-semibold text-slate-700 mb-1">uniqueKey / portalKey</div>
                        <div className="font-mono break-all">{testResult.uniqueKey || testResult.portalKey || '—'}</div>
                      </div>

                      <div className="text-xs text-slate-600">
                        <div className="font-semibold text-slate-700 mb-1">RFQ created</div>
                        <div className="font-mono">{testResult.rfqCreated ? `Yes (${testResult.rfqId})` : 'No'}</div>
                        {testResult.rfqIdFromClient && (
                          <div className="text-xs text-slate-500">
                            client.rfqId: <span className="font-mono">{testResult.rfqIdFromClient}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {testResult.note && <div className="mt-4 text-xs text-slate-500">{testResult.note}</div>}
                  </div>
                </div>
              )}

              <div className="mt-5 flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  Tip: If ShipServ blocks on cookies/headless, watch backend console output.
                </div>

                <div className="flex items-center gap-2">
                  <Button onClick={onCopyTestResult} disabled={!testResult && !testError}>
                    Copy result
                  </Button>

                  <Button variant="primary" onClick={closeTestModal} disabled={!!testRunningId}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
      </Modal>

      {/* Portal Modal */}
      <Modal
        isOpen={portalModalOpen}
        title={portalModalMode === 'create' ? 'Add portal mapping' : 'Edit portal mapping'}
        onClose={closePortalModal}
        maxWidthClass="max-w-[860px]"
      >
            <div>
              <div className="mb-4 text-xs text-slate-600">
                Match an incoming email or link to a portal client and a credential strategy.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Match Type */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Match type</label>
                  <select
                    value={portalForm.matchType}
                    onChange={(e) => setPortalForm((p) => ({ ...p, matchType: e.target.value as PortalMatchType }))}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    {matchTypes.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <div className="mt-1 text-xs text-slate-500">
                    {matchTypes.find((m) => m.value === portalForm.matchType)?.hint}
                  </div>
                </div>

                {/* Match Value */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Match value</label>
                  <input
                    value={portalForm.matchValue}
                    onChange={(e) => setPortalForm((p) => ({ ...p, matchValue: e.target.value }))}
                    placeholder={portalForm.matchType === 'fromEmail' ? 'e.g. noreply@mail.procureship.com' : 'e.g. procureship.com'}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 font-mono"
                  />
                </div>

                {/* Portal Type */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Portal system</label>
                  <select
                    value={portalForm.portalType}
                    onChange={(e) => setPortalForm((p) => ({ ...p, portalType: e.target.value as PortalType }))}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    {portalTypes.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Enabled */}
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
                    <input
                      type="checkbox"
                      checked={portalForm.enabled}
                      onChange={(e) => setPortalForm((p) => ({ ...p, enabled: e.target.checked }))}
                      className="h-4 w-4 accent-primary-500"
                    />
                    Enabled
                  </label>
                </div>

                {/* Credential Mode */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Credential mode</label>
                  <select
                    value={portalForm.credentialsMode}
                    onChange={(e) => setPortalForm((p) => ({ ...p, credentialsMode: e.target.value as CredentialsMode }))}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  >
                    {credentialsModes.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <div className="mt-1 text-xs text-slate-500">
                    {credentialsModes.find((m) => m.value === portalForm.credentialsMode)?.hint}
                  </div>
                </div>

                {/* Portal URL Override */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Portal URL override (optional)</label>
                  <input
                    value={portalForm.portalUrlOverride}
                    onChange={(e) => setPortalForm((p) => ({ ...p, portalUrlOverride: e.target.value }))}
                    placeholder="e.g. https://procureship.com/home"
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 font-mono"
                  />
                  <div className="mt-1 text-xs text-slate-500">Used when email portal link is missing or unreliable.</div>
                </div>

                {/* Username */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Username (optional)</label>
                  <input
                    value={portalForm.username}
                    onChange={(e) => setPortalForm((p) => ({ ...p, username: e.target.value }))}
                    placeholder="Portal username"
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    {portalModalMode === 'create' ? 'Password' : 'Update Password'}{' '}
                    <span className="text-xs text-slate-500 font-normal">(leave blank to keep unchanged)</span>
                  </label>
                  <input
                    type="password"
                    value={portalForm.password}
                    onChange={(e) => setPortalForm((p) => ({ ...p, password: e.target.value }))}
                    placeholder={portalModalMode === 'create' ? 'Set password' : '••••••••'}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  />
                </div>

                {/* One-time patterns */}
                {showOneTimeSection && (
                  <div className="md:col-span-2 border border-slate-200 rounded-md p-4 bg-slate-50/50">
                    <div className="mb-2">
                      <div className="text-xs font-semibold text-slate-700">One-time Credential Patterns (optional)</div>
                      <div className="text-xs text-slate-500">
                        Provide regex patterns if the email body format is non-standard. Leave blank to use default heuristics.
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Username Regex</label>
                        <input
                          value={portalForm.oneTimePatterns.usernameRegex}
                          onChange={(e) =>
                            setPortalForm((p) => ({
                              ...p,
                              oneTimePatterns: { ...p.oneTimePatterns, usernameRegex: e.target.value },
                            }))
                          }
                          placeholder='e.g. "Username\\s*[:\\-]\\s*(\\S+)"'
                          className="w-full border border-slate-200 rounded-md px-3 py-2 text-xs font-mono focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Password Regex</label>
                        <input
                          value={portalForm.oneTimePatterns.passwordRegex}
                          onChange={(e) =>
                            setPortalForm((p) => ({
                              ...p,
                              oneTimePatterns: { ...p.oneTimePatterns, passwordRegex: e.target.value },
                            }))
                          }
                          placeholder='e.g. "Password\\s*[:\\-]\\s*(\\S+)"'
                          className="w-full border border-slate-200 rounded-md px-3 py-2 text-xs font-mono focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">OTP Regex (optional)</label>
                        <input
                          value={portalForm.oneTimePatterns.otpRegex}
                          onChange={(e) =>
                            setPortalForm((p) => ({
                              ...p,
                              oneTimePatterns: { ...p.oneTimePatterns, otpRegex: e.target.value },
                            }))
                          }
                          placeholder='e.g. "OTP\\s*[:\\-]\\s*(\\d{4,8})"'
                          className="w-full border border-slate-200 rounded-md px-3 py-2 text-xs font-mono focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Notes (optional)</label>
                  <textarea
                    value={portalForm.notes}
                    onChange={(e) => setPortalForm((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="Any notes about this mapping (client name, portal quirks, etc.)"
                    className="w-full min-h-[80px] border border-slate-200 rounded-md px-3 py-2 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                  />
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  Tip: Use <b>fromEmail</b> for most precise matching. Use <b>fromDomain</b> when sender varies.
                </div>

                <div className="flex items-center gap-2">
                  <Button onClick={closePortalModal} disabled={portalSaving}>
                    Cancel
                  </Button>

                  <Button variant="primary" onClick={savePortalSetting} loading={portalSaving}>
                    {portalSaving ? 'Saving…' : 'Save mapping'}
                  </Button>
                </div>
              </div>

              <div className="mt-3 text-xs text-slate-500">
                Password is stored encrypted on backend and never returned by the API.
              </div>
            </div>
      </Modal>
    </div>
  );
};


